/**
 * sqlite3mt WAL Frame Writer — §96 §7 (native SAB layout)
 *
 * Writes merged pages directly as WAL frames to SAB2, bypassing sqlite3_exec.
 * Uses the native sqlite3mt SAB layout (sab-layout.ts) instead of the legacy
 * SabMemoryManager model.
 *
 * Handles:
 *   - E1: Cumulative WAL checksums (via wal-checksum.ts)
 *   - E2: WAL salt re-read (salt may change between exports)
 *   - E5: WalIndexHdr double-write (offset 0 + offset 48)
 *   - E7: Page 1 header preservation. Logical page bytes are owned by the
 *     merge/reconcile layer because those exact bytes are hashed into the
 *     public root before the WAL append is published.
 *   - E15: WAL-Index hash table update (aPgno + linear probe)
 *   - C1: Frame dedup (only last frame per pgno in batch)
 *
 * Replaces the legacy walFrameWriter.ts which depended on SabMemoryManager.
 *
 * @see wal.c L960-993 (walEncodeFrame)
 * @see §96 §7 (WAL File Format)
 */

import {
    WAL_HDR_SIZE,
    WAL_FRAME_HDR_SIZE,
    parseWalHeader,
    computeFrameChecksum,
    readLastFrameChecksum,
    buildFrameHeader,
    buildWalHeader,
    type WalHeader,
} from './wal-checksum'
import {
    SHM_DATA_SIZE,
    ensureSab2WalCapacity,
    sab2MaxWalByteLength,
    sab2SizeView,
    sab2DataView,
    sab3ShmView,
    sab3LockView,
    shmLockRead,
} from './sab-layout'

// ── WAL-Index Constants (wal.c) ─────────────────────────────────

/** wal.c line 278: version number for WalIndexHdr.iVersion */
const WALINDEX_MAX_VERSION = 3007000

/** Hash table entries per SHM page (wal.c HASHTABLE_NSLOT) */
const HASHTABLE_NSLOT = 8192

/** Max page numbers per hash table page (wal.c HASHTABLE_NPAGE) */
const HASHTABLE_NPAGE = 4096

/** WalIndexHdr size (48 bytes per copy, 2 copies + 40B ckpt = 136 bytes total) */
const WALINDEX_HDR_SIZE = 136

const WAL_CKPT_WORD_BASE = 96 / 4
const WAL_CKPT_READMARK_WORD0 = WAL_CKPT_WORD_BASE + 1

/** Hash table page header: aPgno array at start, then hash table */
const HASHTABLE_NPAGE_ONE = HASHTABLE_NPAGE - (WALINDEX_HDR_SIZE / 4)

// ── Types ───────────────────────────────────────────────────────

export interface FrameWriteResult {
    /** Number of frames written */
    framesWritten: number
    /** New mxFrame after write */
    newMxFrame: number
    /** New WAL data size (bytes) */
    newWalSize: number
    /** Running checksum after all frames */
    finalCksum: [number, number]
}

export interface Page1MergeFields {
    /** file_change_counter (offset 24, 4 bytes BE) */
    changeCounter: number
    /** version_valid_for (offset 92, 4 bytes BE) */
    versionValidFor: number
    /** database_size (offset 28, 4 bytes BE) */
    dbSize: number
}

/**
 * SAB views needed for WAL frame writing.
 * Extracted from the sqlite3mt SAB layout, not from SabMemoryManager.
 */
export interface WalFrameSabViews {
    /** SAB2 backing buffer for capacity growth. */
    sab2: SharedArrayBuffer
    /** SAB2 WAL size Int32 view (1 element) */
    walSizeView: Int32Array
    /** SAB2 WAL data bytes view (starting after size prefix) */
    walDataView: Uint8Array
    /** SAB3 SHM data bytes view */
    shmView: Uint8Array
    /** SAB3 WAL lock region */
    shmLockView: Int32Array
    /** Page size in bytes */
    pageSize: number
    /** Maximum WAL capacity in bytes (data portion only) */
    maxWalCapacity: number
    /** SAB4 buffer for memory fence (optional, only needed for hdr update) */
    sab4?: SharedArrayBuffer | ArrayBuffer
}

export interface WalFrameAppendCapacity {
    canAppend: boolean
    currentWalBytes: number
    requiredWalBytes: number
    maxWalBytes: number
    frameCount: number
}

// ── Helpers to create WalFrameSabViews from raw SABs ────────────

/**
 * Create WalFrameSabViews from individual SAB buffers.
 */
export function createWalFrameSabViews(
    sab2: SharedArrayBuffer,
    sab3: SharedArrayBuffer,
    pageSize: number,
    sab4?: SharedArrayBuffer | ArrayBuffer,
): WalFrameSabViews {
    return {
        sab2,
        walSizeView: sab2SizeView(sab2),
        walDataView: sab2DataView(sab2),
        shmView: sab3ShmView(sab3),
        shmLockView: sab3LockView(sab3),
        pageSize,
        maxWalCapacity: sab2MaxWalByteLength(sab2),
        sab4,
    }
}

// ── Page 1 Header Merge (E7) ────────────────────────────────────

/**
 * Merge Page 1 header fields per §96 §6.4.
 *
 * Page 1 is special: first 100 bytes are the DB header, B-tree starts at 100.
 * Fields that change on every write:
 *   offset 24: file_change_counter → max(local, remote) + 1
 *   offset 28: database_size → max(local, remote)
 *   offset 92: version_valid_for → same as change_counter
 *
 * If page 1 is in the import set, we MUST update these fields.
 */
export function mergePage1Header(
    pageData: Uint8Array,
    currentPage1: Uint8Array | null,
): Page1MergeFields {
    const view = new DataView(pageData.buffer, pageData.byteOffset, pageData.byteLength)

    // Read current values from the imported page
    const importedCounter = view.getUint32(24, false)
    const importedDbSize = view.getUint32(28, false)

    if (currentPage1) {
        const curView = new DataView(
            currentPage1.buffer, currentPage1.byteOffset, currentPage1.byteLength,
        )
        const currentCounter = curView.getUint32(24, false)
        const currentDbSize = curView.getUint32(28, false)

        const newCounter = Math.max(importedCounter, currentCounter) + 1
        const newDbSize = Math.max(importedDbSize, currentDbSize)

        // Apply to page data
        view.setUint32(24, newCounter, false)
        view.setUint32(28, newDbSize, false)
        view.setUint32(92, newCounter, false)  // version_valid_for

        return { changeCounter: newCounter, versionValidFor: newCounter, dbSize: newDbSize }
    }

    // No current page 1 → just increment counter
    const newCounter = importedCounter + 1
    view.setUint32(24, newCounter, false)
    view.setUint32(92, newCounter, false)

    return { changeCounter: newCounter, versionValidFor: newCounter, dbSize: importedDbSize }
}

// ── SAB view helpers ────────────────────────────────────────────

function getWalDataSize(views: WalFrameSabViews): number {
    return Atomics.load(views.walSizeView, 0)
}

function setWalDataSize(views: WalFrameSabViews, value: number): void {
    Atomics.store(views.walSizeView, 0, value)
}

export function ensureWalFrameAppendCapacity(
    views: WalFrameSabViews,
    frameCount: number,
): WalFrameAppendCapacity {
    const normalizedFrameCount = Math.max(0, Math.trunc(frameCount))
    const currentWalBytes = getWalDataSize(views)
    const baseWalBytes = currentWalBytes > 0 ? currentWalBytes : WAL_HDR_SIZE
    const requiredWalBytes = baseWalBytes + normalizedFrameCount * (WAL_FRAME_HDR_SIZE + views.pageSize)
    const canAppend = ensureSab2WalCapacity(views.sab2, requiredWalBytes)
    if (canAppend && views.walDataView.byteLength < requiredWalBytes) {
        views.walDataView = sab2DataView(views.sab2)
        views.maxWalCapacity = sab2MaxWalByteLength(views.sab2)
    }
    return {
        canAppend,
        currentWalBytes,
        requiredWalBytes,
        maxWalBytes: sab2MaxWalByteLength(views.sab2),
        frameCount: normalizedFrameCount,
    }
}

// ── WAL Frame Append ────────────────────────────────────────────

/**
 * Append merged pages as WAL frames to SAB2.
 *
 * This is the core of WAL-native import (replaces Phase 3 close/reopen).
 * Connections stay open. Readers are NOT blocked — they only see new frames
 * when mxFrame is updated in WalIndexHdr (step after this).
 */
export function appendWalFrames(
    views: WalFrameSabViews,
    pages: Map<number, Uint8Array>,
    dbPageCount: number,
    _currentPage1: Uint8Array | null = null,
): FrameWriteResult {
    if (pages.size === 0) {
        return {
            framesWritten: 0,
            newMxFrame: 0,
            newWalSize: getWalDataSize(views),
            finalCksum: [0, 0],
        }
    }

    // C1: Dedup — Map already deduplicates (last set() wins per key)
    // Convert to sorted array for deterministic order
    const sortedPages = [...pages.entries()].sort((a, b) => a[0] - b[0])

    const pageSize = views.pageSize

    // Read WAL header from SAB2 (E2: salt may have changed)
    let wal = views.walDataView
    let walHeader = parseWalHeader(wal.subarray(0, WAL_HDR_SIZE))
    if (!walHeader) {
        // WAL header missing (e.g., fresh DB, or importFullDb reset SAB2).
        // Initialize with a valid header so we can append frames.
        const salt1 = (Math.random() * 0xFFFFFFFF) >>> 0
        const salt2 = (Math.random() * 0xFFFFFFFF) >>> 0
        const hdr = buildWalHeader(pageSize, 0, salt1, salt2, true)
        // Write WAL header at the start of the data view
        wal.set(hdr, 0)
        setWalDataSize(views, WAL_HDR_SIZE)
        walHeader = parseWalHeader(wal.subarray(0, WAL_HDR_SIZE))!
    }

    // Read current mxFrame from WalIndexHdr in SHM
    const shm = views.shmView
    let currentMxFrame = 0
    if (shm.byteLength >= 20) {
        const shmView = new DataView(shm.buffer, shm.byteOffset, shm.byteLength)
        currentMxFrame = shmView.getUint32(16, true) // WalIndexHdr.mxFrame (offset 16, LE)
    }

    // E1: Read last frame's running checksum as seed
    let cksum = readLastFrameChecksum(wal, currentMxFrame, pageSize)

    const capacity = ensureWalFrameAppendCapacity(views, sortedPages.length)
    const frameSize = WAL_FRAME_HDR_SIZE + pageSize
    if (!capacity.canAppend) {
        throw new Error(
            `walFrameWriter: WAL capacity exceeded. Need ${capacity.requiredWalBytes} bytes, ` +
            `have ${capacity.maxWalBytes}.`,
        )
    }
    if (wal.byteLength < capacity.requiredWalBytes) {
        wal = sab2DataView(views.sab2)
        views.walDataView = wal
        views.maxWalCapacity = sab2MaxWalByteLength(views.sab2)
    }

    let writeOffset = capacity.currentWalBytes > 0 ? capacity.currentWalBytes : WAL_HDR_SIZE

    // Write frames
    const newFrames: { pgno: number; frameIdx: number }[] = []

    for (let i = 0; i < sortedPages.length; i++) {
        const [pgno, pageData] = sortedPages[i]
        const frameIdx = currentMxFrame + i + 1

        // Last frame in batch gets nTruncate = dbPageCount (commit marker)
        const nTruncate = (i === sortedPages.length - 1) ? dbPageCount : 0

        // Compute cumulative checksum for this frame
        cksum = computeFrameChecksum(
            walHeader.nativeCksum,
            pgno,
            nTruncate,
            pageData,
            cksum,
        )

        // Build and write frame header (24 bytes)
        const frameHdr = buildFrameHeader(
            pgno,
            nTruncate,
            walHeader.salt1,
            walHeader.salt2,
            cksum,
        )

        wal.set(frameHdr, writeOffset)
        wal.set(pageData, writeOffset + WAL_FRAME_HDR_SIZE)

        newFrames.push({ pgno, frameIdx })
        writeOffset += frameSize
    }

    // Update WAL data size
    setWalDataSize(views, writeOffset)

    return {
        framesWritten: sortedPages.length,
        newMxFrame: currentMxFrame + sortedPages.length,
        newWalSize: writeOffset,
        finalCksum: cksum,
    }
}

// ── WAL-Index Hash Table Update (E15) ───────────────────────────

/**
 * Update WAL-Index hash table in SAB3 with new frame entries.
 *
 * WAL-Index structure (wal.c L222-280):
 *   SHM page 0: WalIndexHdr (136B header: 2×48B hdr + 40B CkptInfo) + hash0
 *   SHM page 1+: full hash pages
 *
 * Each hash page contains:
 *   aPgno[HASHTABLE_NPAGE]: page numbers for frames in this segment
 *   aHash[HASHTABLE_NSLOT]: hash table (value = aPgno index + 1, 0 = empty)
 *
 * Hash function: (pgno * 383) % HASHTABLE_NSLOT (wal.c L266)
 * Collision: linear probe (wal.c L268-269)
 */
export function updateWalIndex(
    views: WalFrameSabViews,
    newFrames: { pgno: number; frameIdx: number }[],
): void {
    if (newFrames.length === 0) return

    const shm = views.shmView

    for (const { pgno, frameIdx } of newFrames) {
        // Which SHM page does this frame belong to?
        let shmPageIdx: number
        let pageBase: number
        let nPage: number

        if (frameIdx <= HASHTABLE_NPAGE_ONE) {
            shmPageIdx = 0
            pageBase = 0
            nPage = HASHTABLE_NPAGE_ONE
        } else {
            const adjusted = frameIdx - HASHTABLE_NPAGE_ONE - 1
            shmPageIdx = 1 + Math.floor(adjusted / HASHTABLE_NPAGE)
            pageBase = HASHTABLE_NPAGE_ONE + (shmPageIdx - 1) * HASHTABLE_NPAGE
            nPage = HASHTABLE_NPAGE
        }

        // Check SHM bounds
        const regionStart = shmPageIdx * SHM_DATA_SIZE
        if (regionStart + SHM_DATA_SIZE > shm.byteLength) {
            continue
        }

        const regionView = new DataView(
            shm.buffer, shm.byteOffset + regionStart, SHM_DATA_SIZE
        )

        // aPgno array starts at beginning of region (for page 0: after WalIndexHdr)
        const aPgnoOffset = shmPageIdx === 0 ? WALINDEX_HDR_SIZE : 0
        const hashOffset = aPgnoOffset + nPage * 4 // hash table follows aPgno

        // Index within this page's aPgno array
        const entryIdx = frameIdx - pageBase - 1

        // Write pgno to aPgno array
        regionView.setUint32(aPgnoOffset + entryIdx * 4, pgno, true) // LE

        // Hash: (pgno * 383) % HASHTABLE_NSLOT
        let slot = ((pgno * 383) >>> 0) % HASHTABLE_NSLOT

        // Linear probe to find empty slot
        for (let probe = 0; probe < HASHTABLE_NSLOT; probe++) {
            const hashEntryOffset = hashOffset + slot * 2 // u16 entries
            const existing = regionView.getUint16(hashEntryOffset, true) // LE
            if (existing === 0) {
                // Empty slot — write (entryIdx + 1)
                regionView.setUint16(hashEntryOffset, entryIdx + 1, true)
                break
            }
            // Collision — linear probe
            slot = (slot + 1) % HASHTABLE_NSLOT
        }
    }
}

// ── WalIndexHdr Update (E5) ─────────────────────────────────────

/**
 * Update WalIndexHdr with new mxFrame.
 *
 * E5: Double-write protocol — write copy[1] first, then barrier, then copy[0].
 * This ensures readers always see a consistent header.
 *
 * WalIndexHdr layout (48 bytes):
 *   [0..3]   iVersion (WALINDEX_MAX_VERSION = 3007000)
 *   [4..7]   unused (0)
 *   [8..11]  iChange (bumped every commit)
 *   [12]     isInit (1)
 *   [13]     bigEndCksum
 *   [14..15] szPage ((nByte&0xff00)|(nByte>>16), LE — wal.c L1571)
 *   [16..19] mxFrame (last valid frame index)
 *   [20..23] nPage (total DB pages)
 *   [24..31] aFrameCksum (running cksum from last frame)
 *   [32..39] aSalt (salt1, salt2)
 *   [40..47] aCksum (header checksum of WalIndexHdr itself)
 *
 * Copy[0] at offset 0, Copy[1] at offset 48.
 */
export function updateWalIndexHdr(
    views: WalFrameSabViews,
    newMxFrame: number,
    dbSize: number,
    finalCksum: [number, number],
    walHeader: WalHeader,
): void {
    const shm = views.shmView
    if (shm.byteLength < WALINDEX_HDR_SIZE) {
        throw new Error('walFrameWriter: SHM region too small for WalIndexHdr update')
    }

    const view = new DataView(shm.buffer, shm.byteOffset, shm.byteLength)

    // Read current iChange to increment
    const currentIChange = view.getUint32(8, true)
    const newIChange = currentIChange + 1

    const pageSize = views.pageSize

    // Build header data (48 bytes)
    const writeHdr = (offset: number): void => {
        view.setUint32(offset + 0, WALINDEX_MAX_VERSION, true) // iVersion
        view.setUint32(offset + 4, 0, true)                    // unused
        view.setUint32(offset + 8, newIChange, true)           // iChange (triggers pChanged)
        view.setUint8(offset + 12, 1)                          // isInit = 1
        view.setUint8(offset + 13, walHeader.nativeCksum ? 0 : 1) // bigEndCksum
        // szPage: encoded per wal.c walIndexWriteHdr L1571:
        const szPageEncoded = (pageSize & 0xff00) | (pageSize >>> 16)
        view.setUint16(offset + 14, szPageEncoded, true)       // szPage (LE)
        view.setUint32(offset + 16, newMxFrame, true)          // mxFrame
        view.setUint32(offset + 20, dbSize, true)              // nPage
        view.setUint32(offset + 24, finalCksum[0], true)       // aFrameCksum[0]
        view.setUint32(offset + 28, finalCksum[1], true)       // aFrameCksum[1]
        view.setUint32(offset + 32, walHeader.salt1, true)     // aSalt[0]
        view.setUint32(offset + 36, walHeader.salt2, true)     // aSalt[1]

        // Compute WalIndexHdr checksum (over first 40 bytes)
        const hdrData = new Uint8Array(shm.buffer, shm.byteOffset + offset, 40)

        let s1 = 0 >>> 0, s2 = 0 >>> 0
        const dv = new DataView(hdrData.buffer, hdrData.byteOffset, 40)
        for (let i = 0; i < 40; i += 8) {
            s1 = (s1 + dv.getUint32(i, true) + s2) >>> 0
            s2 = (s2 + dv.getUint32(i + 4, true) + s1) >>> 0
        }
        view.setUint32(offset + 40, s1, true) // aCksum[0]
        view.setUint32(offset + 44, s2, true) // aCksum[1]
    }

    // E5: Write copy[1] FIRST (offset 48), then barrier, then copy[0] (offset 0)
    writeHdr(48)

    // Memory fence between copies — critical for cross-worker visibility
    if (views.sab4 != null) {
        const ctrlView = new Int32Array(views.sab4)
        Atomics.store(ctrlView, 0, Atomics.load(ctrlView, 0))
    }

    writeHdr(0)

    seedReadableWalTipReadMark(views, newMxFrame)
}

function seedReadableWalTipReadMark(views: WalFrameSabViews, newMxFrame: number): void {
    if (newMxFrame <= 0) {
        return
    }

    const shmWords = new Int32Array(
        views.shmView.buffer,
        views.shmView.byteOffset,
        views.shmView.byteLength / Int32Array.BYTES_PER_ELEMENT,
    )

    for (let readMark = 1; readMark < 5; readMark += 1) {
        const lockIndex = shmLockRead(readMark)
        if (Atomics.compareExchange(views.shmLockView, lockIndex, 0, -1) !== 0) {
            continue
        }

        try {
            Atomics.store(shmWords, WAL_CKPT_READMARK_WORD0 + readMark, newMxFrame)
            return
        } finally {
            Atomics.store(views.shmLockView, lockIndex, 0)
        }
    }
}
